/**
 * @file types.ts
 * @description Shared types for all Server Actions. Provides consistent
 *              response shapes for form submissions across the application.
 * @author William Donaldson
 * @last_modified 2025-12-27
 */

/**
 * Machine-readable error codes for programmatic handling by client hooks.
 * WHY: Typed constant object prevents string drift between server actions
 * and the useWarmupRetry hook — both import from this single source.
 */
export const FORM_ERROR_CODES = {
  SERVER_WARMUP: "SERVER_WARMUP",
  /**
   * The record moved between the page loading and the save arriving, and this save
   * would have written over the change. See @/server/concurrency/stale-write.
   */
  STALE_WRITE: "STALE_WRITE",
} as const;

/**
 * Name of the hidden form field carrying the record's `updatedAt` as of page load.
 *
 * WHY IT LIVES BESIDE THE RESPONSE TYPES: it is written by a client form and read by a
 * server action that never import each other. A typo on either side would silently
 * disable the whole check — the action would see no expected timestamp and fall open —
 * so both sides read the name from here. This module is the existing home for the
 * form/action contract and is already imported from both.
 */
export const EXPECTED_UPDATED_AT_FIELD = "expectedUpdatedAt";

/**
 * One field a blocked save would have overwritten.
 *
 * WHY BOTH VALUES: "someone else edited this" is not actionable on its own. Showing what
 * the record says now beside what the save would have set lets the user decide whether
 * to keep their edit, take the other one, or merge — without hunting for the difference.
 *
 * WHY IT IS SAFE TO SEND: these are values from a record the caller is already permitted
 * to read and was about to write. Field NAMES only ever reach audit metadata; the values
 * stay in the response.
 */
export interface FieldConflict {
  /** Form field name, so the UI can line the row up with its input. */
  field: string;
  /** Human-readable label for the field. */
  label: string;
  /** What the record holds now, after the other person's change. */
  current: string;
  /** What this save would have written. */
  submitted: string;
}

/**
 * Standard response type for form-based Server Actions.
 * Used with useActionState hook in React 19.
 *
 * - ok: true = action succeeded (may redirect before returning)
 * - ok: false + formError = general error (show in Alert)
 * - ok: false + fieldErrors = validation errors (show per field)
 * - values = submitted form values, returned on error to preserve user input
 * - data = optional additional data to return on success (e.g., counts, IDs)
 * - errorCode = machine-readable code for client-side programmatic handling
 */
export interface FormActionState<T = Record<string, unknown>> {
  ok: boolean;
  formError: string | null;
  fieldErrors: Record<string, string>;
  values?: Record<string, string>;
  data?: T;
  errorCode?: (typeof FORM_ERROR_CODES)[keyof typeof FORM_ERROR_CODES];
  /**
   * Fields a STALE_WRITE would have overwritten. Present only with that error code.
   */
  conflicts?: FieldConflict[];
}

/**
 * Initial state for useActionState. Use this as the second argument
 * to avoid defining the same object in every form component.
 */
export const INITIAL_FORM_STATE: FormActionState = {
  ok: true,
  formError: null,
  fieldErrors: {},
};

/**
 * Simple action result for non-form actions (button clicks, etc.)
 * Use when you don't need field-level validation errors.
 */
export interface SimpleActionResult {
  success: boolean;
  error?: string;
}
